import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/features/auth/stores/authStore';
import type { ApiResponse, LoginData } from '@/features/auth/services/authApi';
import { extractPermissionsFromToken, extractRolesFromToken } from '@/shared/utils/jwtUtils';
import { normalizeError } from '@/shared/errors/normalizeError';
import type { ApiErrorEnvelope } from '@/shared/types/apiError';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

type RetryableAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Shared with useIdleTimeout's LS_LOGGED_OUT — writing it notifies other tabs to log out too.
const LS_LOGGED_OUT = 'sessionLoggedOut';
let loggedOut = false;
let refreshPromise: Promise<boolean> | null = null;
const ROTATION_WAIT_MS = 1_500;
const ROTATION_POLL_MS = 50;
const REJECTED_SESSION_STATUSES = [400, 401, 403];

export const isRejectedSessionError = (error: unknown) =>
  axios.isAxiosError(error) && REJECTED_SESSION_STATUSES.includes(error.response?.status ?? 0);

/** A competing tab can reject our old token before it persists its replacement. */
const waitForOtherTabRotation = async (usedToken: string): Promise<boolean> => {
  const deadline = Date.now() + ROTATION_WAIT_MS;
  while (true) {
    const storedToken = localStorage.getItem('refreshToken');
    if (!storedToken || !useAuthStore.getState().isAuthenticated) return false;
    if (storedToken !== usedToken) {
      useAuthStore.getState().initializeAuth();
      return true;
    }
    if (Date.now() >= deadline) return false;
    await new Promise<void>((resolve) => setTimeout(resolve, ROTATION_POLL_MS));
  }
};

/** One rotation per tab; the browser lock also serializes rotations across tabs. */
export const refreshAuthSession = (): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;
  const lockManager = navigator.locks;
  const hasBrowserLocks = Boolean(lockManager?.request);
  const rotate = async (): Promise<boolean> => {
    const current = useAuthStore.getState();
    if (!current.refreshToken) return false;
    // Another tab may have rotated while this tab waited for the lock.
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) return false;
    if (storedRefreshToken !== current.refreshToken) {
      current.initializeAuth();
      return true;
    }
    let response: ApiResponse<LoginData>;
    try {
      ({ data: response } = await apiClient.post<ApiResponse<LoginData>>(
        '/Users/refresh-token', { refreshToken: current.refreshToken }
      ));
    } catch (error) {
      // Browser LockManager is unavailable in some contexts. If a competing tab
      // completed rotation first, use its stored credentials instead of logging out.
      if (hasBrowserLocks) {
        const rotatedToken = localStorage.getItem('refreshToken');
        if (rotatedToken && rotatedToken !== current.refreshToken) {
          current.initializeAuth();
          return true;
        }
      } else if (await waitForOtherTabRotation(current.refreshToken)) {
        return true;
      }
      throw error;
    }
    if (!response.success || !response.data) return false;
    // Logout (or another tab's rotation) may have happened while the request
    // was in flight. Never restore a session that the user already ended.
    const latestRefreshToken = localStorage.getItem('refreshToken');
    if (!latestRefreshToken || !useAuthStore.getState().isAuthenticated) return false;
    if (latestRefreshToken !== current.refreshToken) {
      current.initializeAuth();
      return true;
    }
    const data = response.data;
    const roles = extractRolesFromToken(data.token);
    const permissions = data.permissions?.length
      ? data.permissions
      : current.permissions.length ? current.permissions : extractPermissionsFromToken(data.token);
    useAuthStore.getState().setAuth(
      { ...data.user, roles: roles.length ? roles : (current.user?.roles ?? []) },
      data.token, permissions, data.refreshToken, data.expiresAt
    );
    return true;
  };
  refreshPromise = (async () => {
    if (lockManager) {
      return lockManager.request('farmsmart-session-refresh', rotate);
    }
    return rotate();
  })().finally(() => { refreshPromise = null; });
  return refreshPromise;
};

/**
 * Hard-end the session: revoke server-side (best effort), clear local auth,
 * notify other tabs, and redirect to /login. Guarded so parallel 401s only
 * trigger one redirect.
 */
const forceLogout = () => {
  if (loggedOut) return;
  loggedOut = true;

  const { refreshToken, logout } = useAuthStore.getState();
  if (refreshToken) {
    // Best-effort revoke; the logout URL is excluded from this handler so it can't recurse.
    apiClient.post('/Users/logout', { refreshToken }).catch(() => {});
  }
  logout();
  localStorage.setItem(LS_LOGGED_OUT, String(Date.now()));
  window.location.href = '/login';
};

apiClient.interceptors.request.use((config) => {
  const { token } = useAuthStore.getState();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Response interceptor: recover once from an expired access token, then attach the
// normalized ErrorEnvelope to the rejected error so callers (useApiError) don't re-normalize.
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    surfaceWarnings(response);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

    if (
      originalRequest?.url?.includes('/Users/login') ||
      originalRequest?.url?.includes('/Users/refresh-token') ||
      originalRequest?.url?.includes('/Users/logout')
    ) {
      attachEnvelope(error);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const tokenUsed = originalRequest.headers?.Authorization;
        const latestToken = useAuthStore.getState().token;
        if ((latestToken && tokenUsed !== `Bearer ${latestToken}`) || await refreshAuthSession()) {
          return apiClient(originalRequest);
        }
        forceLogout();
      } catch (refreshError) {
        // A transport/provider failure during refresh is not proof the session
        // was revoked. Preserve the form and let the user retry the request.
        if (isRejectedSessionError(refreshError)) {
          forceLogout();
        }
      }
      attachEnvelope(error);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest?._retry) {
      forceLogout();
    }

    attachEnvelope(error);
    return Promise.reject(error);
  }
);

const attachEnvelope = (error: AxiosError) => {
  (error as AxiosError & { envelope?: ApiErrorEnvelope }).envelope = normalizeError(error);
};

/**
 * Surface non-blocking warnings from a successful response as Sonner toasts so the
 * user sees background notices like "FY 2027 was created automatically" without
 * the call site having to remember to handle them. Backend emits these via
 * Result.WithWarnings → ApiResponse.Warnings.
 */
const surfaceWarnings = (response: AxiosResponse) => {
  const body = response.data as { warnings?: { code: string; message: string }[] | null } | undefined;
  if (!body?.warnings || body.warnings.length === 0) return;
  for (const warning of body.warnings) {
    toast.warning(warning.message);
  }
};

export default apiClient;
